import React, { useCallback, useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import Textbox from 'carbon-react/lib/components/textbox';
import Switch from 'carbon-react/lib/components/switch';
import Typography from 'carbon-react/lib/components/typography';
import { Sidebar } from '@futrli/ui-components';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { UILoaderSpinner } from '@futrli/ui-components';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';

interface Props {
  isOpen?: boolean;
  onToggle: () => void;
  onSubmit: (workspaceName: string, isDefaultWorkspace: boolean) => void;
  isLoading: boolean;
  workspace?: WorkspacesListingItem;
  isSuperAdmin: boolean;
  isDefaultWorkspace: boolean;
}

interface SidebarContentProps {
  workspace?: WorkspacesListingItem;
  handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name: string;
}

interface NewSidebarContentProps {
  workspace?: WorkspacesListingItem;
  showWarning: boolean;
  isSuperAdmin: boolean;
  checked: boolean;
  handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMakeDefaultWorkspaceChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name: string;
}

const SidebarContent = ({ workspace, handleNameChange, name }: SidebarContentProps) => {
  const intl = useIntl();

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <FormattedMessage id="workspaces.current_workspace_name" defaultMessage="Current workspace name" />
      </div>
      <Typography mb="16px" fontSize="16px" fontWeight="500">
        {workspace?.name ||
          intl.formatMessage(
            { id: 'landing.default_workspace_name', defaultMessage: "{name}'s workspace" },
            { name: workspace?.owner.first_name }
          )}
      </Typography>
      <Textbox
        autoFocus
        label={intl.formatMessage({
          id: 'workspaces.new_workspace_name',
          defaultMessage: 'New workspace name'
        })}
        value={name}
        onChange={handleNameChange}
        style={{ marginRight: 18 }}
        placeholder={intl.formatMessage({
          id: 'workspaces.enter_new_workspace_name',
          defaultMessage: 'Enter new workspace name'
        })}
      />
    </div>
  );
};

export const NewSidebarContent = ({
  workspace,
  showWarning,
  checked,
  isSuperAdmin,
  handleNameChange,
  handleMakeDefaultWorkspaceChange,
  name
}: NewSidebarContentProps) => {
  const intl = useIntl();

  return (
    <div>
      <Typography mb="4px" variant="span">
        <FormattedMessage id="workspaces.current_workspace_name" defaultMessage="Current workspace name" />
      </Typography>
      <Typography mb="16px" fontSize="16px" fontWeight="500">
        {workspace?.name ||
          intl.formatMessage(
            { id: 'landing.default_workspace_name', defaultMessage: "{name}'s workspace" },
            { name: workspace?.owner.first_name }
          )}
      </Typography>
      {isSuperAdmin && (
        <Textbox
          label={intl.formatMessage({
            id: 'workspaces.new_workspace_name',
            defaultMessage: 'New workspace name'
          })}
          value={name}
          onChange={handleNameChange}
          style={{ marginRight: 18 }}
          placeholder={intl.formatMessage({
            id: 'workspaces.enter_new_workspace_name',
            defaultMessage: 'Enter new workspace name'
          })}
        />
      )}
      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center' }}>
        <Switch checked={checked} onChange={handleMakeDefaultWorkspaceChange} mb={0} mr="16px" />
        <Typography variant="span" fontWeight="500" fontSize="16px" mb={0}>
          <FormattedMessage id="workspaces.make_default_workspace" defaultMessage="Make default workspace" />
        </Typography>
      </div>
      {showWarning && (
        <Typography mb={0} mt="12px">
          <FormattedMessage
            id="workspaces.default_workspace_warning"
            defaultMessage="Default workspaces makes finding organisations easier - we recommend you add your most used workspace as the default."
          />
        </Typography>
      )}
    </div>
  );
};

export const WorkspaceSettingsSidebar = ({
  isOpen,
  onToggle,
  onSubmit,
  isLoading,
  workspace,
  isSuperAdmin,
  isDefaultWorkspace
}: Props) => {
  const [name, setName] = useState('');
  const [checked, setChecked] = useState(isDefaultWorkspace);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    setChecked(isDefaultWorkspace);
  }, [isDefaultWorkspace]);

  const flags = useFlags();

  const intl = useIntl();

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const handleMakeDefaultWorkspaceChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Show warning when user toggles off default workspace
      if (isDefaultWorkspace && !event.target.checked) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
      setChecked(event.target.checked);
    },
    [isDefaultWorkspace]
  );

  return (
    <Sidebar
      open={!!isOpen}
      header={
        <Typography variant="h2" fontWeight="700">
          {flags.defaultWorkspace
            ? intl.formatMessage({ id: 'workspaces.workspace_settings', defaultMessage: 'Workspace settings' })
            : intl.formatMessage({ id: 'workspaces.change_workspace_name', defaultMessage: 'Change workspace name' })}
        </Typography>
      }
      onCancel={onToggle}
    >
      {isLoading ? (
        <UILoaderSpinner />
      ) : (
        <Form
          stickyFooter
          leftSideButtons={
            <Button onClick={onToggle}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
          }
          saveButton={
            <Button buttonType="primary" onClick={() => onSubmit(name, checked)}>
              {flags.defaultWorkspace ? (
                <FormattedMessage id="common.save" defaultMessage="Save" />
              ) : (
                <FormattedMessage id="common.save_new_name" defaultMessage="Save new name" />
              )}
            </Button>
          }
          onSubmit={() => onSubmit(name, checked)}
        >
          {flags.defaultWorkspace ? (
            <NewSidebarContent
              isSuperAdmin={isSuperAdmin}
              checked={checked}
              workspace={workspace}
              handleNameChange={handleNameChange}
              name={name}
              handleMakeDefaultWorkspaceChange={handleMakeDefaultWorkspaceChange}
              showWarning={showWarning}
            />
          ) : (
            <SidebarContent workspace={workspace} handleNameChange={handleNameChange} name={name} />
          )}
        </Form>
      )}
    </Sidebar>
  );
};
